import React from 'react';
import { FC, Fragment } from 'react';
import { OphirFilterModel, FilterNights, FilterPackage } from '../../ts/classes/OphirFilterModel';
import { useSearchParams } from 'react-router-dom';
import { formatPriceCurrency } from '@bepro-travel/fe.utils';
import { Checkbox, SelectCustom, Tooltip } from '@bepro-travel/fe.ui';
import { Range } from 'react-range';
import { useTranslation } from 'react-i18next';

interface OphirFilterProps {
  filter: OphirFilterModel;
  onChange: (filter: OphirFilterModel) => void;
  onChangePrice: (filter: OphirFilterModel) => void;
  onReset: () => void;
}

const OphirFilter: FC<OphirFilterProps> = ({ filter, onChange, onReset, onChangePrice }) => {

  const [t] = useTranslation() as unknown as [(key: string) => string];
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [urlParams] = useSearchParams();

  const onPressNights = (value: FilterNights) => {
    const newNights = filter.nights.map((rating) => ({
      ...rating,
      checked: value.count === rating.count ? !rating.checked : rating.checked,
    }));
    const newFilter = { ...filter, nights: newNights };
    onChange(newFilter);
  };

  const onPressPackage = (value: FilterPackage) => {
    const newPackages = filter.packages.map((rating) => ({
      ...rating,
      checked: value.name === rating.name ? !rating.checked : rating.checked,
    }));
    const newFilter = { ...filter, packages: newPackages };
    onChange(newFilter);
  };

  const showResetFilter = () => {
    return (
      filter.prices.value[0] !== filter.prices.min ||
      filter.prices.value[1] !== filter.prices.max ||
      filter.nights.some((item) => item.checked) ||
      filter.packages.some((item) => item.checked) ||
      !!filter.selectedHotel ||
      !!filter.selectedMonths.length ||
      !!filter.selectedCountries.length
    );
  };

  return (
    <div className="oph-filter default-shadow">
      {urlParams.get('allCountries') === 'true' && (
        <Fragment>
          <div className="text-bold mb-5">{t('country')}</div>

          <div className="mb-20">
            <SelectCustom
              options={filter.countries}
              value={filter.selectedCountries}
              onChange={(value) => onChange({ ...filter, selectedCountries: value })}
              isMulti={true}
            />
          </div>
        </Fragment>
      )}

      {urlParams.get('allDates') === 'true' && (
        <Fragment>
          <div className="text-bold mb-5">{t('date')}</div>

          <div className="mb-20">
            <SelectCustom
              options={filter.months}
              value={filter.selectedMonths}
              onChange={(value) => onChange({ ...filter, selectedMonths: value })}
              isMulti={true}
            />
          </div>
        </Fragment>
      )}

      <div className="text-bold mb-5">{t('tour.tour')}</div>

      <div className="mb-20">
        <SelectCustom
          options={filter.hotels}
          value={filter.selectedHotel}
          onChange={(value) => onChange({ ...filter, selectedHotel: value })}
        />
      </div>

      <div className="text-bold mb-5">{t('tour.nghts')}</div>
      <div className="mb-20">
        {filter.nights.map((night) => (
          <div className="flex-between text-sm mb-5" key={night.count}>
            <Checkbox
              onChange={() => onPressNights(night)}
              label={`${night.count} + ${t('tour.nghts')?.toLowerCase()}`}
              checked={night.checked}
            />
            <span className="price">{formatPriceCurrency(night.price, night.currency)}</span>
          </div>
        ))}
      </div>
      {filter.prices.min !== filter.prices.max && isFinite(filter.prices.min) && (
        <Fragment>
          <div className="text-bold mb-5">{t('price')}</div>
          <div className="mb-20">
            <div className="flight-filter-time text-lh-sm">
              <span>{filter.prices.value[0]}</span>
              <span>{filter.prices.value[1]}</span>
            </div>
            <Range
              step={1}
              rtl={localStorage.getItem('language') === 'he'}
              min={filter.prices.min}
              max={filter.prices.max}
              onFinalChange={() =>
                onChange({ ...filter, prices: { ...filter.prices, value: filter.prices.value } })
              }
              values={filter.prices.value}
              onChange={(value) =>
                onChangePrice({
                  ...filter,
                  prices: { ...filter.prices, value: value as [number, number] },
                })
              }
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  style={{
                    ...props,
                    height: '4px',
                    width: 'calc(100% - 16px)',
                    margin: '0 8px',
                    backgroundColor: '#ccc',
                  }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props,
                    position: 'absolute',
                    height: '16px',
                    width: '16px',
                    borderRadius: '50%',
                    backgroundColor: colorContrast,
                  }}
                />
              )}
            />
          </div>
        </Fragment>
      )}

      <Fragment>
        <div className="text-bold mb-5">{t('tour.packages')}</div>
        <div className="mb-20">
          {filter.packages.map((item) => (
            <div className="flex-between text-sm mb-5" key={item.name}>
              <Checkbox
                onChange={() => onPressPackage(item)}
                label={item.name}
                checked={item.checked}
              />
              <span className="price">{formatPriceCurrency(item.price, item.currency)}</span>
            </div>
          ))}
        </div>
      </Fragment>

      {showResetFilter() && (
        <div className="oph-filter-reset">
          <Tooltip
            content={t('reset_filters')}
            position="top"
            children={
              <span className="color-danger text-xsm text-underline" onClick={onReset}>
                {t('reset')}
              </span>
            }
          ></Tooltip>
        </div>
      )}
    </div>
  );
};

export default OphirFilter;
